'use client'
import {useLocale, useTranslations} from 'next-intl';
import {useState} from 'react';
import {useErrorAlert} from '@/hooks/useSweetAlert';
import {confirmResetPasswordAction} from '@/actions/auth';
import {getGqlErrorToast} from '@/libs/api/errorHandler';
import {redirect} from '@/i18n/routing';
import {CALLBACK_URL_PARAM, PRIVATE_ROUTE_NAME} from '@/libs/constants/common.constants';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {z} from 'zod';
import {ZodValidator} from '@/libs/zod.validator';
import {useSearchParams} from 'next/navigation';
import {useUserContext} from '@/providers/UserContext';
import {useCartContext} from '@/providers/CartContext';
import {useWishlistContext} from '@/providers/WshlistContext';
import {parseCartProducts, parseWishlistProducts} from '@/libs/product';

export default function ChangePasswordPrimary({ email, code, currency }) {
  const v = useTranslations('validators');
  const e = useTranslations('error.message');
  const t = useTranslations('auth');
  
  const query = useSearchParams();
  const callbackUrl = query.get(CALLBACK_URL_PARAM) ?? PRIVATE_ROUTE_NAME;
  const { saveUser } = useUserContext();
  const { cartProducts, updateCartLocalStorage } = useCartContext();
  const { wishlistProducts, updateWishlistLocalStorage } = useWishlistContext();
  
  const [loading, setLoading] = useState(false);
  const [passwordType, setPasswordType] = useState('password');
  const [passwordIcon, setPasswordIcon] = useState('eye-slash');
  const [confirmPasswordType, setConfirmPasswordType] = useState('password');
  const [confirmPasswordIcon, setConfirmPasswordIcon] = useState('eye-slash');
  
  const toast = useErrorAlert();
  const locale = useLocale();
  const passwordChars = 8;
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: zodResolver(
        z.object({
          password: ZodValidator.password(v, passwordChars),
          confirmPassword: ZodValidator.password(v, passwordChars)
        }).superRefine(({ confirmPassword, password }, ctx) => {
          if (confirmPassword !== password) {
            ctx.addIssue({
              code: 'custom',
              message: v('passwordMustMatch'),
              path: ['confirmPassword']
            });
          }
        })
    ),
    defaultValues: {
      password: '',
      confirmPassword: ''
    }
  });
  
  const handleTogglePassword = () => {
    if (passwordType === 'password'){
      setPasswordIcon('eye');
      setPasswordType('text')
    } else {
      setPasswordIcon('eye-slash')
      setPasswordType('password')
    }
  }
  
  const handleToggleConfirmPassword = () => {
    if (confirmPasswordType === 'password'){
      setConfirmPasswordIcon('eye');
      setConfirmPasswordType('text')
    } else {
      setConfirmPasswordIcon('eye-slash')
      setConfirmPasswordType('password')
    }
  }
  
  const onConfirm = async (data) => {
    setLoading(true);
    const result = await confirmResetPasswordAction({
      email,
      code,
      password: data.password,
      locale,
      currency,
      cart: cartProducts?.map(cp => ({
        productId: cp.product.id,
        quantity: cp.quantity
      })) ?? [],
      wishlist: wishlistProducts?.map(wp => ({
        productId: wp.product.id
      })) ?? []
    });
    
    if (result.error) {
      toast(getGqlErrorToast(result.error.errors, e));
      setLoading(false);
      return;
    }
    if (result.data) {
      saveUser(result.data);
      
      const {freshData, totalPrice} = parseCartProducts(result.cart, null);
      updateCartLocalStorage(freshData, totalPrice);
      const {freshData: freshWishlist} = parseWishlistProducts(result.wishlist);
      updateWishlistLocalStorage(freshWishlist);
      
      redirect({
        href: callbackUrl,
        locale
      });
      setLoading(false);
    }
  };
  
  return (
    <div className="ltn__login-area" style={{minHeight: '70vh', alignContent: 'center'}}>
      <div className="container">
        <div className="section-title-area text-center">
          <h1 className="section-title">
            {t('changePasswordTitle')}
          </h1>
          <p>
            {t('changePasswordDescription', { char: passwordChars })}
          </p>
        </div>
        <div className="row">
          <div className="col-lg-6 offset-lg-3">
            <div className="account-login-inner">
              <form onSubmit={handleSubmit(onConfirm)} className="ltn__form-box contact-form-box">
                <div className={`form-group ${errors.password ? 'has-error' : ''}`}>
                  <div className="password-field">
                    <input
                        id="password"
                        type={passwordType}
                        placeholder={`${t('password')}*`}
                        {...register('password')}
                    />
                    <span className="icon-wrapper" onClick={handleTogglePassword}>
                      <i className={`fa-regular fa-${passwordIcon}`}></i>
                    </span>
                  </div>
                  {errors.password && <span className="error-message">{errors.password.message}</span>}
                </div>
                <div className={`form-group ${errors.confirmPassword ? 'has-error' : ''}`}>
                  <div className="password-field">
                    <input
                        id="confirmPassword"
                        type={confirmPasswordType}
                        placeholder={`${t('confirmPassword')}*`}
                        {...register('confirmPassword')}
                    />
                    <span className="icon-wrapper" onClick={handleToggleConfirmPassword}>
                      <i className={`fa-regular fa-${confirmPasswordIcon}`}></i>
                    </span>
                  </div>
                  {errors.confirmPassword && <span className="error-message">{errors.confirmPassword.message}</span>}
                </div>
                <div className="btn-wrapper">
                  <button
                      className="theme-btn-1 btn reverse-color btn-block text-uppercase"
                      type="submit"
                      disabled={loading || isSubmitting}
                  >
                    {isSubmitting || loading ? t('saving') : t('savePassword')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
